import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { type OrganizationFeatures } from '@libraries/typescript/api_client';

/**
 * Hook to get a feature flag value, with URL override support.
 *
 * In non-production environments, URL parameters take precedence over
 * organization features. Add
 * `?feature_name=true` or `?feature_name=false` to override any feature flag.
 *
 * Reads `window.location.search` directly rather than `useRouterState`: the
 * router hook throws when a caller is mounted above `<RouterProvider>` (which
 * blanked the whole app when CallProvider used it), and overrides are applied
 * by loading a URL, so a non-reactive read is all this needs.
 *
 * @param featureName - The name of the feature flag to check
 * @param features - The organization's features object (from useOrganization)
 * @param defaultValue - The default value if not set in URL or features (defaults to false)
 * @returns Whether the feature is enabled
 *
 * @example
 * ```tsx
 * const { data: organization } = useOrganization(organizationId, { reason: 'my-component' });
 * const lastNameSortingEnabled = useFeatureFlag('last_name_sorting_enabled', organization?.features);
 *
 * // With custom default:
 * const newFeature = useFeatureFlag('new_feature', organization?.features, true);
 *
 * // Override via URL: ?last_name_sorting_enabled=true
 * ```
 */
export function useFeatureFlag(
    featureName: keyof OrganizationFeatures,
    features: OrganizationFeatures | null | undefined,
    defaultValue: boolean = false,
): boolean {
    // Check URL override first (takes precedence in non-production)
    if (getRunEnvironment() !== 'production') {
        const urlValue = new URLSearchParams(window.location.search)
            .get(String(featureName))
            ?.toLowerCase();
        if (urlValue === 'true') {
            return true;
        }
        if (urlValue === 'false') {
            return false;
        }
    }

    // Fall back to organization features
    const orgValue = features?.[featureName];
    if (typeof orgValue === 'boolean') {
        return orgValue;
    }

    return defaultValue;
}
